/**
 * Browser-side presence + invite client. A single module-level connection is
 * shared across the app (presence is global, not per-component). Mirrors the
 * protocol in `server/src/core/presenceCore.ts`.
 *
 * @module features/p2p-chat/lib/presence
 */

export interface PresenceInvite {
  readonly fromUserId: string;
  readonly fromName: string;
  readonly roomCode: string;
  readonly ts: number;
}

export interface PresenceCallbacks {
  readonly onConnected?: (connected: boolean) => void;
  readonly onPresence?: (onlineUserIds: readonly string[]) => void;
  readonly onInvite?: (invite: PresenceInvite) => void;
}

export interface PresenceConnection {
  /** Send an invite to a user. Returns false if the socket is unavailable. */
  sendInvite(toUserId: string, roomCode: string): boolean;
  /**
   * Ask the relay whether a chat room still exists (is still joinable). Used to
   * detect ended/destroyed chats before entering a session from an invite.
   * Resolves `{ exists: false, peers: 0 }` on timeout (unknown ⇒ treat as
   * ended; conservative, only applied at invite-activation never to live
   * sessions).
   */
  queryRoom(roomCode: string): Promise<RoomStatus>;
  /** Tear down the connection. */
  close(): void;
}

/** Result of a {@link PresenceConnection.queryRoom} round-trip. */
export interface RoomStatus {
  readonly exists: boolean;
  readonly peers: number;
}

interface OpenPresenceOptions {
  readonly wsUrl: string;
  readonly userId: string;
  readonly displayName: string;
  readonly callbacks: PresenceCallbacks;
}

const RECONNECT_DELAY_MS = 2000;
const ROOM_QUERY_TIMEOUT_MS = 3000;

/** The single active connection (presence is global). */
let active: {
  connection: PresenceConnection;
  socket: WebSocket | null;
} | null = null;

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

/**
 * Open (or replace) the global presence connection. Automatically reconnects
 * until {@link PresenceConnection.close} is called.
 */
export function openPresenceConnection(
  opts: OpenPresenceOptions
): PresenceConnection {
  // Replace any prior connection.
  active?.connection.close();

  const { wsUrl, userId, displayName, callbacks } = opts;
  const url = `${wsUrl}/presence`;
  let socket: WebSocket | null = null;
  let closed = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  const outbound: string[] = [];

  /** Pending room-status resolvers keyed by room code (multiple allowed). */
  const pendingQueries = new Map<string, Array<(status: RoomStatus) => void>>();

  const flush = (): void => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    while (outbound.length > 0) {
      const frame = outbound.shift();
      if (frame !== undefined) socket.send(frame);
    }
  };

  const sendFrame = (frame: string): boolean => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(frame);
      return true;
    }
    outbound.push(frame);
    return false;
  };

  const connect = (): void => {
    if (closed) return;
    socket = new WebSocket(url);

    socket.onopen = (): void => {
      sendFrame(JSON.stringify({ type: 'hello', userId, displayName }));
      flush();
      callbacks.onConnected?.(true);
    };

    socket.onmessage = (evt: MessageEvent): void => {
      if (typeof evt.data !== 'string') return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(evt.data);
      } catch {
        return;
      }
      if (typeof parsed !== 'object' || parsed === null) return;
      const msg = parsed as Record<string, unknown>;
      const type = msg['type'];
      if (type === 'presence' && isStringArray(msg['online'])) {
        callbacks.onPresence?.(msg['online']);
      } else if (type === 'invite') {
        const fromUserId = msg['fromUserId'];
        const fromName = msg['fromName'];
        const roomCode = msg['roomCode'];
        const ts = msg['ts'];
        if (
          typeof fromUserId === 'string' &&
          typeof fromName === 'string' &&
          typeof roomCode === 'string' &&
          typeof ts === 'number'
        ) {
          callbacks.onInvite?.({ fromUserId, fromName, roomCode, ts });
        }
      } else if (type === 'room-status') {
        const roomCode = msg['roomCode'];
        const exists = msg['exists'];
        const peers = msg['peers'];
        if (typeof roomCode === 'string' && typeof exists === 'boolean') {
          const resolvers = pendingQueries.get(roomCode);
          if (resolvers) {
            pendingQueries.delete(roomCode);
            const status: RoomStatus = {
              exists,
              peers: typeof peers === 'number' ? peers : 0,
            };
            for (const resolve of resolvers) resolve(status);
          }
        }
      }
    };

    socket.onclose = (): void => {
      callbacks.onConnected?.(false);
      if (closed) return;
      reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
    };

    socket.onerror = (): void => {
      try {
        socket?.close();
      } catch {
        /* swallow */
      }
    };
  };

  const connection: PresenceConnection = {
    sendInvite(toUserId: string, roomCode: string): boolean {
      return sendFrame(JSON.stringify({ type: 'invite', toUserId, roomCode }));
    },
    queryRoom(roomCode: string): Promise<RoomStatus> {
      return new Promise<RoomStatus>((resolve) => {
        let settled = false;
        const settle = (status: RoomStatus): void => {
          if (settled) return;
          settled = true;
          resolve(status);
        };
        const resolvers = pendingQueries.get(roomCode) ?? [];
        resolvers.push(settle);
        pendingQueries.set(roomCode, resolvers);
        sendFrame(JSON.stringify({ type: 'room-query', roomCode }));
        // Unknown ⇒ treat as ended (conservative; invite-activation only).
        setTimeout(() => {
          const list = pendingQueries.get(roomCode);
          if (list) {
            const next = list.filter((r) => r !== settle);
            if (next.length > 0) pendingQueries.set(roomCode, next);
            else pendingQueries.delete(roomCode);
          }
          settle({ exists: false, peers: 0 });
        }, ROOM_QUERY_TIMEOUT_MS);
      });
    },
    close(): void {
      closed = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      // Resolve any outstanding queries as "ended" so callers never hang.
      for (const resolvers of pendingQueries.values()) {
        for (const resolve of resolvers) resolve({ exists: false, peers: 0 });
      }
      pendingQueries.clear();
      try {
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'bye' }));
        }
        socket?.close();
      } catch {
        /* swallow */
      }
      if (active?.connection === connection) active = null;
    },
  };

  active = { connection, socket: null };
  connect();
  return connection;
}

/** The current global presence connection, if any. */
export function getActivePresenceConnection(): PresenceConnection | null {
  return active?.connection ?? null;
}

/** Convenience: send an invite over the active connection. */
export function sendPresenceInvite(
  toUserId: string,
  roomCode: string
): boolean {
  return active?.connection.sendInvite(toUserId, roomCode) ?? false;
}

/**
 * Convenience: query whether a room still exists over the active connection.
 * Resolves `{ exists: false, peers: 0 }` when there is no active connection
 * (treated as ended — conservative, applied only at invite-activation).
 */
export function queryRoom(
  roomCode: string
): Promise<{ exists: boolean; peers: number }> {
  const connection = active?.connection;
  if (!connection) return Promise.resolve({ exists: false, peers: 0 });
  return connection.queryRoom(roomCode);
}
